import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Grid
} from '@mui/material';
import { Add, Edit, Delete, Medication, TrendingUp, TrendingDown } from '@mui/icons-material';
import { medicamentsAPI } from '../../services/api';

const MedicamentsPage = () => {
  const [medicaments, setMedicaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMedicament, setCurrentMedicament] = useState(null);
  const [formData, setFormData] = useState({
    nom_medicament: '',
    description_medicament: '',
    unite: '',
    prix_unitaire_achat: '',
    prix_unitaire_vente: '',
    prix_vente_technicien: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadMedicaments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMedicaments = async () => {
    try {
      setLoading(true);
      const response = await medicamentsAPI.getAll();
      setMedicaments(response.data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des médicaments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (medicament = null) => {
    if (medicament) {
      setEditMode(true);
      setCurrentMedicament(medicament);
      setFormData({
        nom_medicament: medicament.nom_medicament || '',
        description_medicament: medicament.description_medicament || '',
        unite: medicament.unite || '',
        prix_unitaire_achat: medicament.prix_unitaire_achat || '',
        prix_unitaire_vente: medicament.prix_unitaire_vente || '',
        prix_vente_technicien: medicament.prix_vente_technicien || ''
      });
    } else {
      setEditMode(false);
      setCurrentMedicament(null);
      setFormData({
        nom_medicament: '',
        description_medicament: '',
        unite: '',
        prix_unitaire_achat: '',
        prix_unitaire_vente: '',
        prix_vente_technicien: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentMedicament(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        prix_unitaire_achat: parseFloat(formData.prix_unitaire_achat) || 0,
        prix_unitaire_vente: parseFloat(formData.prix_unitaire_vente) || 0,
        prix_vente_technicien: parseFloat(formData.prix_vente_technicien) || 0
      };

      if (editMode && currentMedicament) {
        await medicamentsAPI.update(currentMedicament.id_medicament, dataToSend);
        showSnackbar('Médicament modifié avec succès', 'success');
      } else {
        await medicamentsAPI.create(dataToSend);
        showSnackbar('Médicament créé avec succès', 'success');
      }
      handleCloseDialog();
      loadMedicaments();
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce médicament ?')) {
      try {
        await medicamentsAPI.delete(id);
        showSnackbar('Médicament supprimé avec succès', 'success');
        loadMedicaments();
      } catch (error) {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const calculateMarge = (achat, vente) => {
    if (!achat || !vente) return 0;
    return ((vente - achat) / achat * 100).toFixed(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <Medication sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestion des Médicaments
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nouveau Médicament
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Unité</strong></TableCell>
              <TableCell><strong>Prix Achat</strong></TableCell>
              <TableCell><strong>Prix Vente</strong></TableCell>
              <TableCell><strong>Prix Technicien</strong></TableCell>
              <TableCell><strong>Marge</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {medicaments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">
                    Aucun médicament trouvé. Cliquez sur "Nouveau Médicament" pour en ajouter un.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              medicaments.map((medicament) => {
                const marge = calculateMarge(medicament.prix_unitaire_achat, medicament.prix_unitaire_vente);
                return (
                  <TableRow key={medicament.id_medicament} hover>
                    <TableCell>{medicament.id_medicament}</TableCell>
                    <TableCell>
                      <strong>{medicament.nom_medicament}</strong>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {medicament.description_medicament}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={medicament.unite} size="small" />
                    </TableCell>
                    <TableCell>{medicament.prix_unitaire_achat} €</TableCell>
                    <TableCell>
                      <strong>{medicament.prix_unitaire_vente} €</strong>
                    </TableCell>
                    <TableCell>{medicament.prix_vente_technicien} €</TableCell>
                    <TableCell>
                      <Chip 
                        label={`${marge}%`}
                        color={marge > 0 ? 'success' : 'error'}
                        size="small"
                        icon={marge > 0 ? <TrendingUp /> : <TrendingDown />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => handleOpenDialog(medicament)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => handleDelete(medicament.id_medicament)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'ajout/modification */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Modifier le médicament' : 'Nouveau médicament'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="nom_medicament"
                  label="Nom du médicament"
                  fullWidth
                  value={formData.nom_medicament}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description_medicament"
                  label="Description"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.description_medicament}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="unite"
                  label="Unité"
                  fullWidth
                  value={formData.unite}
                  onChange={handleChange}
                  required
                  placeholder="ml, comprimés, boîte..."
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="prix_unitaire_achat"
                  label="Prix d'achat (€)"
                  type="number"
                  fullWidth
                  value={formData.prix_unitaire_achat}
                  onChange={handleChange}
                  required
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="prix_unitaire_vente"
                  label="Prix de vente (€)"
                  type="number"
                  fullWidth
                  value={formData.prix_unitaire_vente}
                  onChange={handleChange}
                  required
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="prix_vente_technicien"
                  label="Prix de vente technicien (€)"
                  type="number"
                  fullWidth
                  value={formData.prix_vente_technicien}
                  onChange={handleChange}
                  required
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MedicamentsPage;